import React from "react";
import type { MasspayWithdrawFees } from "../types/payments";
import { formatAmountForCopy, formatTxnAmount } from "../utils/currency";
import { t } from "../i18n";
import "./MasspayFeeTable.css";

// MassPay's own fees + minimums for taking money out of the wallet (server
// lib/masspay/withdrawOptions.js, read from MassPay for this amount) — shown
// BEFORE the money moves (founder 10/29, after his own test: Venmo $3.52 on
// $5, then "Amount is under minimum limit"). Pick A on
// .claude/fan-cashout-fees.html: cheapest bank first with a green tag, then
// Venmo / PayPal / the country's wallets; a row this amount can't use is
// greyed with its minimum. Under $50 one gentle line: small cash-outs lose
// more to fees. The fees are MassPay's (the payee pays them), never ours.

export const SMALL_CASHOUT_USD = 50;

const label = (o: MasspayWithdrawFees["options"][number]) =>
  o.kind === "bank" ? t("connectBank.yourBank") : o.kind === "venmo" ? "Venmo" : o.kind === "paypal" ? "PayPal" : o.name || "";

export default function MasspayFeeTable({ fees }: { fees: MasspayWithdrawFees }) {
  const usd = (n: number) => formatTxnAmount(n, "usd");
  return (
    <div className="mft" role="table" aria-label={t("masspayFees.fees")}>
      {fees.options.map((o, i) => (
        <div className={`mft-row${o.fits ? "" : " mft-row--off"}`} role="row" key={`${o.kind}-${o.name || i}`}>
          <span className="mft-name" role="cell">
            {label(o)}
            {i === 0 && o.kind === "bank" && <i className="mft-cheap">{t("masspayFees.cheapest")}</i>}
          </span>
          <span className="mft-fee" role="cell">
            {t("masspayFees.fee", { amount: usd(o.fee) })}
            {o.min > 0 && <> · {t("masspayFees.from", { amount: formatAmountForCopy(o.min, "usd") })}</>}
          </span>
        </div>
      ))}
    </div>
  );
}

/** "Small cash-outs lose more to fees. Waiting until $50 keeps more of it." — only under $50. */
export function SmallCashoutLine({ amount }: { amount: number }) {
  if (!(amount > 0) || amount >= SMALL_CASHOUT_USD) return null;
  return <p className="mft-small">{t("masspayFees.smallCashout", { amount: formatAmountForCopy(SMALL_CASHOUT_USD, "usd") })}</p>;
}
